import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import "./styles/premium-steps.css";
import "./styles/forms.css";
import App from "./App";
import startVersionPinger from "./utils/versionPinger";

// Suppress ResizeObserver errors - these are benign browser warnings
// that occur during animations and don't affect functionality
const resizeObserverErrorHandler = (e) => {
  if (e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
      e.message === 'ResizeObserver loop limit exceeded') {
    e.stopImmediatePropagation();
    return;
  }
};

window.addEventListener('error', resizeObserverErrorHandler);

// ─── ChunkLoadError auto-recovery ───────────────────────────────
// After a new deploy, old tabs still reference the previous webpack
// chunk hashes. When those chunks fail to load we reload ONCE using a
// sessionStorage guard so we never enter an infinite reload loop.
const CHUNK_RELOAD_KEY = '__credys_chunk_reload__';

const isChunkLoadError = (err) => {
  if (!err) return false;
  const name = err.name || '';
  const msg = err.message || String(err);
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk .* failed/i.test(msg) ||
    /Loading CSS chunk .* failed/i.test(msg) ||
    /Unexpected token '<'/i.test(msg) && /chunk/i.test(msg)
  );
};

const recoverFromChunkError = async () => {
  if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return; // already retried this session
  sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch (_) { /* ignore */ }
  window.location.reload();
};

window.addEventListener('error', (e) => {
  if (isChunkLoadError(e.error || e)) recoverFromChunkError();
});
window.addEventListener('unhandledrejection', (e) => {
  if (isChunkLoadError(e.reason)) recoverFromChunkError();
});

// Clear the reload guard once the page renders successfully so a future
// deploy can trigger a fresh recovery.
window.addEventListener('load', () => {
  setTimeout(() => sessionStorage.removeItem(CHUNK_RELOAD_KEY), 5000);
});

// ─── Blank-page watchdog ────────────────────────────────────────
// If a stale service-worker hands back a cached index.html that refers
// to webpack chunk hashes no longer on disk, the resulting page is blank
// with no ChunkLoadError thrown (the chunk just 404s silently on CRA's
// async import). Detect that scenario once per session and force a clean
// reload with SW + caches cleared.
const BLANK_RECOVERY_KEY = '__credys_blank_recovery__';
window.addEventListener('load', () => {
  setTimeout(async () => {
    const root = document.getElementById('root');
    const rendered = root && root.childElementCount > 0 && (root.innerText || '').trim().length > 0;
    if (rendered) return;
    if (sessionStorage.getItem(BLANK_RECOVERY_KEY)) return;
    sessionStorage.setItem(BLANK_RECOVERY_KEY, '1');
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch (_) { /* ignore */ }
    window.location.reload();
  }, 6000);
});

// Register Service Worker for PWA / offline / push notifications.
// When a new SW is found waiting, dispatch a custom event so the UI
// (App.js) can show an "App updated — reload" toast.
if ('serviceWorker' in navigator) {
  let reloadingForUpdate = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // The new SW has taken control after SKIP_WAITING. Reload once.
    if (reloadingForUpdate) return;
    reloadingForUpdate = true;
    window.location.reload();
  });

  const notifyUpdateReady = (registration) => {
    window.dispatchEvent(
      new CustomEvent('credys:update-available', {
        detail: {
          activate: () => {
            if (registration.waiting) {
              registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            }
          },
        },
      })
    );
  };

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => {
        console.log('[PWA] Service worker registered:', reg.scope);
        // SW already waiting from a previous tab visit
        if (reg.waiting && navigator.serviceWorker.controller) {
          notifyUpdateReady(reg);
        }
        reg.addEventListener('updatefound', () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              // A new version has finished installing while an older one is still active.
              notifyUpdateReady(reg);
            }
          });
        });
      })
      .catch((err) => console.warn('[PWA] Service worker registration failed:', err));
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Start the build-version pinger so tabs auto-refresh on the next deploy.
startVersionPinger();
